import React, { useState, useEffect } from 'react'
import Select from "react-select";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine, 
  faFilter, 
  faDownload,
  faSpinner,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
  faGraduationCap,
  faFileText,
  faUsers,
  faClipboardCheck,
  faEdit,
  faTags,
  faChartBar,
  faChartPie,
  faArrowTrendUp,
  faCalendarAlt,
  faRefresh,
  faTimes,
  faEye,
  faEyeSlash,
  faBook,
  faUserTie,
  faUniversity,
  faChartArea,
  faAward
} from '@fortawesome/free-solid-svg-icons'
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip, ResponsiveContainer } from 'recharts'
import './AnalyticsDashboard.css'

const AnalyticsDashboard = () => {
  // State management
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  
  // Filter state
  const [filters, setFilters] = useState({
    courseFilter: [],  // ✅ changed from string → array
    academicYearFilter: '',
    semesterFilter: '',
    instructorFilter: '',
    programCategoryFilter: '',
    programCommitteeFilter: '',
    reviewStatusFilter: '',
    examTypeFilter: '',         
    examFormatFilter: ''
  });

  const [availableFilters, setAvailableFilters] = useState({
    academic_years: [],
    semesters: [],
    courses: [],
    instructors: [],
    program_categories: [],
    program_committees: [],
    review_statuses: [],
    exam_types: [],        
    exam_formats: [],
  })
  const [showFilters, setShowFilters] = useState(false)
  
  // Analytics data
  const [overviewData, setOverviewData] = useState(null)
  const [usageData, setUsageData] = useState(null)
  const [qualityData, setQualityData] = useState(null)
  const [reviewData, setReviewData] = useState(null)
  const [courseData, setCourseData] = useState(null)
  const [instructorAllotmentData, setInstructorAllotmentData] = useState(null)
  const [rubricCriteriaData, setRubricCriteriaData] = useState(null)
  
  // UI state
  const [activeSection, setActiveSection] = useState('overview')
  const [notifications, setNotifications] = useState([])

  // Load data on component mount
  useEffect(() => {
    loadAvailableFilters()
    loadAllAnalytics()
    loadRubricCriteria()
  }, [])

  // Reload data when filters change
  useEffect(() => {
    if (overviewData) { // Only reload if data has been loaded at least once
      loadAllAnalytics()
      loadRubricCriteria()
    }
  }, [filters])

  const showNotification = (message, type = 'info') => {
    const id = Date.now()
    setNotifications(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id))
    }, 5000)
  }

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id))
  }

  const loadAvailableFilters = async () => {
    try {
      const response = await fetch('/postgres/analytics/filters')
      if (!response.ok) throw new Error('Failed to load filter options')
      const data = await response.json()
      
      // Ensure all filter arrays are initialized
      setAvailableFilters({
        academic_years: data.academic_years || [],
        semesters: data.semesters || [],
        courses: data.courses || [],
        instructors: data.instructors || [],
        program_categories: data.program_categories || [],
        program_committees: data.program_committees || [],
        review_statuses: data.review_statuses || [],   // ✅ Added properly
        exam_types: data.exam_types || [],             // NEW
        exam_formats: data.exam_formats || []          // NEW
      });
    } catch (err) {
      console.error('Error loading filters:', err)
      showNotification('Failed to load filter options', 'error')
      
      // Set default empty arrays on error
      setAvailableFilters({
        academic_years: [],
        semesters: [],
        courses: [],
        instructors: [],
        program_categories: [],
        program_committees: [],
        review_statuses: [],
        exam_types: [],
        exam_formats: []
      })
    }
  }

  const loadRubricCriteria = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.courseFilter.length > 0) params.append('course_filter', filters.courseFilter.join(','));
      if (filters.academicYearFilter) params.append('academic_year_filter', filters.academicYearFilter)
      if (filters.semesterFilter) params.append('semester_filter', filters.semesterFilter)
      if (filters.instructorFilter) params.append('instructor_filter', filters.instructorFilter)
      if (filters.programCategoryFilter) params.append('program_category_filter', filters.programCategoryFilter)
      if (filters.programCommitteeFilter) params.append('program_committee_filter', filters.programCommitteeFilter)
      if (filters.reviewStatusFilter) params.append("review_status_filter", filters.reviewStatusFilter);
      if (filters.examTypeFilter) params.append("exam_type_filter", filters.examTypeFilter);
      if (filters.examFormatFilter) params.append("exam_format_filter", filters.examFormatFilter);
      
      const response = await fetch(`/postgres/analytics/rubric-criteria?${params}`)
      if (!response.ok) throw new Error('Failed to load rubric criteria data')
      
      const data = await response.json()
      setRubricCriteriaData(data)
    } catch (err) {
      console.error('Error loading rubric criteria:', err)
      // Don't show error notification for optional feature
    }
  }

  const loadAllAnalytics = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const params = new URLSearchParams()
      if (filters.courseFilter.length > 0) params.append('course_filter', filters.courseFilter.join(','));
      if (filters.academicYearFilter) params.append('academic_year_filter', filters.academicYearFilter)
      if (filters.semesterFilter) params.append('semester_filter', filters.semesterFilter)
      if (filters.instructorFilter) params.append('instructor_filter', filters.instructorFilter)
      if (filters.programCategoryFilter) params.append('program_category_filter', filters.programCategoryFilter)
      if (filters.programCommitteeFilter) params.append('program_committee_filter', filters.programCommitteeFilter)
      if (filters.reviewStatusFilter) params.append("review_status_filter", filters.reviewStatusFilter);
      if (filters.examTypeFilter) params.append("exam_type_filter", filters.examTypeFilter);
      if (filters.examFormatFilter) params.append("exam_format_filter", filters.examFormatFilter);
      
      const [overviewRes, usageRes, qualityRes, reviewRes, courseRes, instructorAllotmentRes] = await Promise.all([
        fetch(`/postgres/analytics/overview?${params}`),
        fetch(`/postgres/analytics/usage?days=30&${params}`),
        fetch(`/postgres/analytics/quality?${params}`),
        fetch(`/postgres/analytics/review?${params}`),
        fetch(`/postgres/analytics/courses?${params}`),
        fetch(`/postgres/analytics/instructor-allotment?${params}`)
      ])

      if (!overviewRes.ok || !usageRes.ok || !qualityRes.ok || !reviewRes.ok || !courseRes.ok) {
        throw new Error('Failed to load analytics data')
      }

      const [overview, usage, quality, review, course] = await Promise.all([
        overviewRes.json(),
        usageRes.json(),
        qualityRes.json(),
        reviewRes.json(),
        courseRes.json()
      ])

      let instructorAllotment = null
      if (instructorAllotmentRes.ok) {
        instructorAllotment = await instructorAllotmentRes.json()
      } else {
        console.warn('Instructor allotment endpoint unavailable:', instructorAllotmentRes.status)
      }

      setOverviewData(overview)
      setUsageData(usage)
      setQualityData(quality)
      setReviewData(review)
      setCourseData(course)
      setInstructorAllotmentData(instructorAllotment)

    } catch (err) {
      console.error('Error loading analytics:', err)
      setError(err.message)
      showNotification('Failed to load analytics data', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadAllAnalytics()
    setRefreshing(false)
    showNotification('Analytics data refreshed successfully!', 'success')
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      courseFilter: [],
      academicYearFilter: '',
      semesterFilter: '',
      instructorFilter: '',
      programCategoryFilter: '',
      programCommitteeFilter: '',
      reviewStatusFilter: '',
      examTypeFilter: '',
      examFormatFilter: ''
    });
  };

  const exportData = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.courseFilter.length > 0) params.append('course_filter', filters.courseFilter.join(','));
      if (filters.academicYearFilter) params.append('academic_year_filter', filters.academicYearFilter)
      if (filters.semesterFilter) params.append('semester_filter', filters.semesterFilter)
      if (filters.instructorFilter) params.append('instructor_filter', filters.instructorFilter)
      if (filters.programCategoryFilter) params.append('program_category_filter', filters.programCategoryFilter)
      if (filters.programCommitteeFilter) params.append('program_committee_filter', filters.programCommitteeFilter)
      if (filters.reviewStatusFilter) params.append("review_status_filter", filters.reviewStatusFilter);
      if (filters.examTypeFilter) params.append("exam_type_filter", filters.examTypeFilter);
      if (filters.examFormatFilter) params.append("exam_format_filter", filters.examFormatFilter);
      
      const response = await fetch(`/postgres/analytics/export?${params}`)
      if (!response.ok) throw new Error('Failed to export data')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `analytics_report_${new Date().toISOString().split('T')[0]}.pdf`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }
      
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      showNotification('Analytics report exported successfully!', 'success')
    } catch (err) {
      console.error('Error exporting data:', err)
      showNotification('Failed to export report', 'error')
    }
  }

  const exportInstructorAllotmentCsv = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.courseFilter.length > 0) params.append('course_filter', filters.courseFilter.join(','));
      if (filters.academicYearFilter) params.append('academic_year_filter', filters.academicYearFilter)
      if (filters.semesterFilter) params.append('semester_filter', filters.semesterFilter)
      if (filters.instructorFilter) params.append('instructor_filter', filters.instructorFilter)

      const response = await fetch(`/postgres/analytics/instructor-allotment/export?${params}`)
      if (!response.ok) throw new Error('Failed to export instructor allotment CSV')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `instructor_allotment_${new Date().toISOString().split('T')[0]}.csv`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showNotification('Instructor allotment CSV downloaded successfully!', 'success')
    } catch (err) {
      console.error('Error exporting instructor allotment CSV:', err)
      showNotification('Failed to export instructor allotment CSV', 'error')
    }
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  const formatPercentage = (num) => {
    return `${num}%`
  }

  if (loading && !overviewData) {
    return (
      <div className="analytics-container">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: 'var(--primary)' }} />
          <p className="loading-text">Loading analytics dashboard...</p>
        </div>
      </div>
    )
  }

  if (error && !overviewData) {
    return (
      <div className="analytics-container">
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#ef4444' }} />
          <h2 className="error-title">Failed to Load Analytics</h2>
          <p className="error-message">{error}</p>
          <button className="btn btn-primary" onClick={loadAllAnalytics}>
            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.5rem' }} />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-content">
          <div className="header-text">
            <div style={{ fontSize: '3rem', color: 'var(--primary)', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <h1>Analytics Dashboard</h1>
            <p>Comprehensive insights into your question review system usage, quality metrics, and review processes.</p>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn btn-outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faFilter} style={{ marginRight: '0.5rem' }} />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <FontAwesomeIcon icon={refreshing ? faSpinner : faRefresh} spin={refreshing} style={{ marginRight: '0.5rem' }} />
              Refresh
            </button>
            <button 
              className="btn btn-primary"
              onClick={exportData}
            >
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.5rem' }} />
              Export Data
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filters-content">
              <div className="filter-group">
                <label className="filter-label">Course</label>
                <Select
                  isMulti
                  classNamePrefix="filter-select"
                  options={availableFilters.courses.map(course => ({
                    value: course.course_id,
                    label: course.course_name
                  }))}
                  value={
                    filters.courseFilter.length
                      ? availableFilters.courses
                          .filter(c => filters.courseFilter.includes(c.course_id))
                          .map(c => ({ value: c.course_id, label: c.course_name }))
                      : []
                  }
                  placeholder="All Courses"
                  onChange={(selectedOptions) => {
                    handleFilterChange(
                      "courseFilter",
                      selectedOptions ? selectedOptions.map(opt => opt.value) : []
                    );
                  }}
                />
              </div>

              <div className="filter-group">
                <label className="filter-label">Academic Year</label>
                <select
                  className="filter-select"
                  value={filters.academicYearFilter}
                  onChange={(e) => handleFilterChange('academicYearFilter', e.target.value)}
                >
                  <option value="">All Years</option>
                  {availableFilters.academic_years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Semester</label>
                <select
                  className="filter-select"
                  value={filters.semesterFilter}
                  onChange={(e) => handleFilterChange('semesterFilter', e.target.value)}
                >
                  <option value="">All Semesters</option>
                  {availableFilters.semesters.map(semester => (
                    <option key={semester} value={semester}>{semester}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Instructor</label>
                <select
                  className="filter-select"
                  value={filters.instructorFilter}
                  onChange={(e) => handleFilterChange('instructorFilter', e.target.value)}
                >
                  <option value="">All Instructors</option>
                  {availableFilters.instructors.map(instructor => (
                    <option key={instructor} value={instructor}>{instructor}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Program Category</label>
                <select
                  className="filter-select"
                  value={filters.programCategoryFilter}
                  onChange={(e) => handleFilterChange('programCategoryFilter', e.target.value)}
                >
                  <option value="">All Program Categories</option>
                  {availableFilters.program_categories?.map(category => (
                    <option key={category} value={category}>{category}</option>
                  )) || []}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Program Committee</label>
                <select
                  className="filter-select"
                  value={filters.programCommitteeFilter}
                  onChange={(e) => handleFilterChange('programCommitteeFilter', e.target.value)}
                >
                  <option value="">All Program Committees</option>
                  {availableFilters.program_committees?.map(committee => (
                    <option key={committee} value={committee}>{committee}</option>
                  )) || []}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Review Status</label>
                <select
                  className="filter-select"
                  value={filters.reviewStatusFilter}
                  onChange={(e) => handleFilterChange("reviewStatusFilter", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {availableFilters.review_statuses.map(status => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Exam Type */}
              <div className="filter-group">
                <label className="filter-label">Exam Type</label>
                <select
                  className="filter-select"
                  value={filters.examTypeFilter}
                  onChange={(e) => handleFilterChange("examTypeFilter", e.target.value)}
                >
                  <option value="">All Types</option>
                  {availableFilters.exam_types.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>

              {/* Exam Format */}
              <div className="filter-group">
                <label className="filter-label">Exam Format</label>
                <select
                  className="filter-select"
                  value={filters.examFormatFilter}
                  onChange={(e) => handleFilterChange("examFormatFilter", e.target.value)}
                >
                  <option value="">All Formats</option>
                  {availableFilters.exam_formats.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>

              <div className="filter-actions">
                <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.25rem' }} />
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
          >
            <FontAwesomeIcon 
              icon={notification.type === 'error' ? faExclamationTriangle : 
                    notification.type === 'success' ? faCheckCircle : faInfoCircle} 
            />
            <span className="notification-message">{notification.message}</span>
            <button
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
            >
              <FontAwesomeIcon icon={faTimes} size="sm" />
            </button>
          </div>
        ))}
      </div>

      {/* Navigation Tabs */}
      <div className="analytics-nav">
        {[
          { key: 'overview', label: 'Overview', icon: faChartBar },
          { key: 'usage', label: 'Usage Trends', icon: faArrowTrendUp },
          { key: 'quality', label: 'Quality Analytics', icon: faEdit },
          { key: 'final_evaluation', label: 'Final Evaluation', icon: faAward },
          { key: 'review', label: 'Review Process', icon: faClipboardCheck },
          { key: 'instructor_allotment', label: 'Instructor Allotment', icon: faUserTie },
          { key: 'courses', label: 'Course Analytics', icon: faBook }
        ].map(tab => (
          <button
            key={tab.key}
            className={`nav-tab ${activeSection === tab.key ? 'active' : ''}`}
            onClick={() => setActiveSection(tab.key)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Analytics Content */}
      <div className="analytics-content">
        {activeSection === 'overview' && overviewData && (
          <div className="analytics-section">
            <h2 className="section-title">System Overview</h2>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faFileText} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{formatNumber(overviewData.total_question_papers)}</div>
                  <div className="metric-label">Question Papers</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faEdit} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{formatNumber(overviewData.total_questions)}</div>
                  <div className="metric-label">Total Questions</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{formatNumber(overviewData.total_reviews)}</div>
                  <div className="metric-label">Total Reviews</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{formatNumber(overviewData.completed_reviews)}</div>
                  <div className="metric-label">Completed Reviews</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{overviewData.average_quality_score}</div>
                  <div className="metric-label">Avg Quality Score</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{formatNumber(overviewData.active_reviewers)}</div>
                  <div className="metric-label">Active Reviewers</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{formatNumber(overviewData.papers_with_paper_analysis)}</div>
                  <div className="metric-label">Papers with Paper Analysis</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faAward} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{overviewData.average_final_weighted_score}</div>
                  <div className="metric-label">Avg Final Score</div>
                </div>
              </div>

            </div>

            <div className="quality-metrics">
              <div className="quality-card">
                <div className="quality-header">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                  <h3>Review Completion Rate</h3>
                </div>
                <div className="quality-content">
                  <div className="quality-percentage">
                    {formatPercentage(overviewData.review_completion_rate)}
                  </div>
                  <div className="quality-description">
                    Percentage of initiated reviews that have been completed
                  </div>
                  <div className="quality-bar">
                    <div 
                      className="quality-fill"
                      style={{ width: `${overviewData.review_completion_rate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'usage' && usageData && (
          <div className="analytics-section">
            <h2 className="section-title">Usage Trends</h2>
            
            {/* Daily Uploads Chart */}
            <div className="chart-container">
              <h3 className="chart-title">Daily Question Paper Uploads</h3>
              <div className="simple-chart">
                {usageData.daily_uploads.slice(-14).map((day, index) => (
                  <div key={index} className="chart-bar">
                    <div 
                      className="bar-fill"
                      style={{ 
                        height: `${Math.max(day.upload_count * 10, 5)}px`,
                        minHeight: '5px'
                      }}
                      title={`${day.date}: ${day.upload_count} uploads`}
                    />
                    <div className="bar-label">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Course Activity */}
            <div className="table-container">
              <h3 className="table-title">Most Active Courses</h3>
              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Course</div>
                  <div className="table-cell">Papers</div>
                </div>
                {usageData.course_activity.slice(0, 10).map((course, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell">{course.course_name}</div>
                    <div className="table-cell">
                      <span className="activity-badge">{course.paper_count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructor Activity */}
            <div className="table-container">
              <h3 className="table-title">Most Active Instructors</h3>
              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Instructor</div>
                  <div className="table-cell">Uploads</div>
                </div>
                {usageData.instructor_activity.slice(0, 10).map((instructor, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell">{instructor.instructor_name}</div>
                    <div className="table-cell">
                      <span className="activity-badge">{instructor.upload_count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'quality' && qualityData && (
          <div className="analytics-section">
            <h2 className="section-title">Quality Analytics</h2>
            
            {/* Rubric Criteria Spider Diagrams - Shown First */}
            {/* Question-Level Rubric Criteria */}
            {rubricCriteriaData && rubricCriteriaData.question_criteria && rubricCriteriaData.question_criteria.length > 0 && (
              <div className="content-card spider-diagram-card">
                <h3 className="card-title">
                  <FontAwesomeIcon icon={faChartArea} style={{ marginRight: '10px', color: '#d4af37' }} />
                  Question-Level Rubric Criteria Performance
                </h3>
                <p className="card-subtitle">
                  Average scores across question-level rubric criteria (normalized to 0-100 scale)
                  {rubricCriteriaData.total_questions_analyzed > 0 && (
                    <span style={{ marginLeft: '10px', color: '#888' }}>
                      • Based on {rubricCriteriaData.total_questions_analyzed} question{rubricCriteriaData.total_questions_analyzed !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
                <div className="spider-diagram-container">
                  <ResponsiveContainer width="100%" height={450}>
                    <RadarChart data={rubricCriteriaData.question_criteria}>
                      <PolarGrid stroke="#444" />
                      <PolarAngleAxis 
                        dataKey="criterion" 
                        tick={{ fill: '#d4af37', fontSize: 12 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#888' }}
                      />
                      <Radar
                        name="Average Score"
                        dataKey="score"
                        stroke="#d4af37"
                        fill="#d4af37"
                        fillOpacity={0.6}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #d4af37',
                          borderRadius: '8px',
                          color: '#e0e0e0'
                        }}
                        formatter={(value, name, props) => [
                          `${value.toFixed(1)}% (${props.payload.avg_raw_score}/3)`,
                          'Score'
                        ]}
                      />
                      <Legend 
                        wrapperStyle={{ color: '#d4af37' }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="criteria-legend">
                  <h4 style={{ color: '#d4af37', marginBottom: '12px' }}>Criteria Details:</h4>
                  <div className="criteria-grid">
                    {rubricCriteriaData.question_criteria.map((criterion, index) => (
                      <div key={index} className="criterion-item">
                        <span className="criterion-name">{criterion.criterion}:</span>
                        <span className="criterion-score">
                          {criterion.score.toFixed(1)}% ({criterion.avg_raw_score}/3)
                        </span>
                        <span className="criterion-count">({criterion.count} questions)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Paper-Level Rubric Criteria */}
            {rubricCriteriaData && rubricCriteriaData.paper_criteria && rubricCriteriaData.paper_criteria.length > 0 && (
              <div className="content-card spider-diagram-card">
                <h3 className="card-title">
                  <FontAwesomeIcon icon={faChartArea} style={{ marginRight: '10px', color: '#4a90e2' }} />
                  Paper-Level Rubric Criteria Performance
                </h3>
                <p className="card-subtitle">
                  Average scores across paper-level rubric criteria (normalized to 0-100 scale)
                  {rubricCriteriaData.total_papers_analyzed > 0 && (
                    <span style={{ marginLeft: '10px', color: '#888' }}>
                      • Based on {rubricCriteriaData.total_papers_analyzed} paper{rubricCriteriaData.total_papers_analyzed !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
                <div className="spider-diagram-container">
                  <ResponsiveContainer width="100%" height={450}>
                    <RadarChart data={rubricCriteriaData.paper_criteria}>
                      <PolarGrid stroke="#444" />
                      <PolarAngleAxis 
                        dataKey="criterion" 
                        tick={{ fill: '#4a90e2', fontSize: 12 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#888' }}
                      />
                      <Radar
                        name="Average Score"
                        dataKey="score"
                        stroke="#4a90e2"
                        fill="#4a90e2"
                        fillOpacity={0.6}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #4a90e2',
                          borderRadius: '8px',
                          color: '#e0e0e0'
                        }}
                        formatter={(value, name, props) => [
                          `${value.toFixed(1)}% (${props.payload.avg_raw_score}/3)`,
                          'Score'
                        ]}
                      />
                      <Legend 
                        wrapperStyle={{ color: '#4a90e2' }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="criteria-legend">
                  <h4 style={{ color: '#4a90e2', marginBottom: '12px' }}>Criteria Details:</h4>
                  <div className="criteria-grid">
                    {rubricCriteriaData.paper_criteria.map((criterion, index) => (
                      <div key={index} className="criterion-item" style={{ borderLeft: '3px solid #4a90e2' }}>
                        <span className="criterion-name">{criterion.criterion}:</span>
                        <span className="criterion-score" style={{ color: '#4a90e2', background: 'rgba(74, 144, 226, 0.15)' }}>
                          {criterion.score.toFixed(1)}% ({criterion.avg_raw_score}/3)
                        </span>
                        <span className="criterion-count">({criterion.count} papers)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quality Score Distribution and Bloom's Taxonomy - Shown After Spider Diagrams */}
            <div className="content-grid">
              <div className="content-card">
                <h3 className="card-title">Quality Score Distribution</h3>
                <div className="score-chart">
                  {qualityData.quality_score_distribution.map((score, index) => (
                    <div key={index} className="score-item">
                      <div className="score-range">{score.score_range}</div>
                      <div className="score-bar">
                        <div 
                          className="score-fill"
                          style={{ 
                            width: `${(score.count / Math.max(...qualityData.quality_score_distribution.map(s => s.count))) * 100}%`,
                            backgroundColor: score.score_range.includes('Excellent') ? '#4caf50' :
                                           score.score_range.includes('Good') ? '#8bc34a' :
                                           score.score_range.includes('Average') ? '#ff9800' :
                                           score.score_range.includes('Below') ? '#ff5722' : '#f44336'
                          }}
                        />
                      </div>
                      <div className="score-count">{score.count}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="content-card">
                <h3 className="card-title">Bloom's Taxonomy Distribution</h3>
                <div className="blooms-chart">
                  {qualityData.blooms_taxonomy_distribution.map((bloom, index) => (
                    <div key={index} className="bloom-item">
                      <div className="bloom-label">{bloom.level_name}</div>
                      <div className="bloom-bar">
                        <div 
                          className="bloom-fill"
                          style={{ width: `${(bloom.count / Math.max(...qualityData.blooms_taxonomy_distribution.map(b => b.count))) * 100}%` }}
                        />
                      </div>
                      <div className="bloom-count">{bloom.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Course Quality Comparison */}
            <div className="table-container">
              <h3 className="table-title">Course Quality Comparison</h3>
              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Course</div>
                  <div className="table-cell">Avg Quality</div>
                  <div className="table-cell">Papers</div>
                </div>
                {qualityData.course_quality_comparison.slice(0, 10).map((course, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell">{course.course_name}</div>
                    <div className="table-cell">
                      <span className="quality-badge">{course.avg_quality}</span>
                    </div>
                    <div className="table-cell">{course.paper_count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'final_evaluation' && qualityData && (
          <div className="analytics-section">
            <h2 className="section-title">Final Evaluation Framework Analytics</h2>
            
            {/* Final Score Distribution */}
            <div className="content-grid">
              <div className="content-card">
                <h3 className="card-title">Final Weighted Score Distribution</h3>
                <div className="score-chart">
                  {qualityData.final_score_distribution.map((score, index) => (
                    <div key={index} className="score-item">
                      <div className="score-range">{score.score_range}</div>
                      <div className="score-bar">
                        <div 
                          className="score-fill"
                          style={{ 
                            width: `${(score.count / Math.max(...qualityData.final_score_distribution.map(s => s.count))) * 100}%`,
                            backgroundColor: score.score_range.includes('Excellent') ? '#4caf50' :
                                           score.score_range.includes('Very Good') ? '#8bc34a' :
                                           score.score_range.includes('Acceptable') ? '#ff9800' :
                                           score.score_range.includes('Needs Improvement') ? '#ff5722' : '#f44336'
                          }}
                        />
                      </div>
                      <div className="score-count">{score.count}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="content-card">
                <h3 className="card-title">Rating Distribution</h3>
                <div className="rating-chart">
                  {qualityData.rating_distribution.map((rating, index) => (
                    <div key={index} className="rating-item">
                      <div className="rating-label">{rating.rating}</div>
                      <div className="rating-bar">
                        <div 
                          className="rating-fill"
                          style={{ 
                            width: `${(rating.count / Math.max(...qualityData.rating_distribution.map(r => r.count))) * 100}%`,
                            backgroundColor: rating.rating === 'Excellent' ? '#4caf50' :
                                           rating.rating === 'Very Good' ? '#8bc34a' :
                                           rating.rating === 'Acceptable' ? '#ff9800' :
                                           rating.rating === 'Needs Improvement' ? '#ff5722' : '#f44336'
                          }}
                        />
                      </div>
                      <div className="rating-count">{rating.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scoring Framework Explanation */}
            <div className="content-card">
              <h3 className="card-title">Scoring Framework Breakdown</h3>
              <div className="scoring-explanation">
                <div className="scoring-component">
                  <div className="component-header">
                    <span className="component-label">Question Level Analysis</span>
                    <span className="component-weight">70% Weight</span>
                  </div>
                  <div className="component-description">
                    Individual question criteria: Clarity, Complexity, Relevance, Specificity, Engagement
                  </div>
                </div>
                <div className="scoring-component">
                  <div className="component-header">
                    <span className="component-label">Paper Level Analysis</span>
                    <span className="component-weight">30% Weight</span>
                  </div>
                  <div className="component-description">
                    Overall paper criteria: Coverage of Course Outcomes, Cognitive Level Balance, Difficulty Appropriateness, Question Variety, Structure & Instructions
                  </div>
                </div>
                <div className="scoring-component final">
                  <div className="component-header">
                    <span className="component-label">Final Weighted Score</span>
                    <span className="component-weight">100% Total</span>
                  </div>
                  <div className="component-description">
                    Combined score (0-100) that determines the final rating and recommendation
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'review' && reviewData && (
          <div className="analytics-section">
            <h2 className="section-title">Review Process Analytics</h2>
            
            {/* Professor Review Statistics */}
            <div className="table-container">
              <h3 className="table-title">Professor Review Statistics</h3>
              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Professor</div>
                  <div className="table-cell">Question Papers</div>
                  <div className="table-cell">Questions Reviewed</div>
                </div>
                {reviewData.professor_review_stats.slice(0, 10).map((professor, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell">{professor.professor_name}</div>
                    <div className="table-cell">
                      <span className="activity-badge">{professor.question_papers_reviewed}</span>
                    </div>
                    <div className="table-cell">
                      <span className="activity-badge">{professor.questions_reviewed}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI vs Human Agreement */}
            <div className="summary-card">
              <h3 className="card-title">AI vs Human Review Agreement (Question Level)</h3>
              <div className="summary-content">
                <div className="summary-stat">
                  <div className="stat-value">{reviewData.agreement_rates.total_reviews}</div>
                  <div className="stat-label">Total Question Reviews</div>
                </div>
                <div className="summary-stat">
                  <div className="stat-value">{reviewData.agreement_rates.agreed_reviews}</div>
                  <div className="stat-label">Agreed Reviews</div>
                </div>
                <div className="summary-stat">
                  <div className="stat-value">{formatPercentage(reviewData.agreement_rates.agreement_rate)}</div>
                  <div className="stat-label">Agreement Rate</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'courses' && courseData && (
          <div className="analytics-section">
            <h2 className="section-title">Course Analytics</h2>
            
            {/* Course Performance */}
            <div className="table-container">
              <h3 className="table-title">Course Performance</h3>
              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Course</div>
                  <div className="table-cell">Papers</div>
                  <div className="table-cell">Avg Quality</div>
                  <div className="table-cell">Final Score</div>
                  <div className="table-cell">Questions</div>
                  <div className="table-cell">Avg Marks</div>
                </div>
                {courseData.course_performance.slice(0, 10).map((course, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell">{course.course_name}</div>
                    <div className="table-cell">{course.total_papers}</div>
                    <div className="table-cell">
                      <span className="quality-badge">{course.avg_quality}</span>
                    </div>
                    <div className="table-cell">
                      <span className="final-score-badge">{course.avg_final_score}</span>
                    </div>
                    <div className="table-cell">{course.total_questions}</div>
                    <div className="table-cell">{course.avg_marks}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Academic Year Trends */}
            <div className="chart-container">
              <h3 className="chart-title">Academic Year Trends</h3>
              <div className="academic-year-trends">
                {courseData.academic_year_trends.map((year, index) => (
                  <div key={index} className="year-trend-item">
                    <div className="year-info">
                      <h4 className="year-name">{year.academic_year}</h4>
                      <div className="year-stats">
                        <div className="year-stat">
                          <div className="year-stat-value">{year.paper_count}</div>
                          <div className="year-stat-label">Papers</div>
                        </div>
                      </div>
                    </div>
                    <div className="year-quality">
                      <div className="quality-score">{year.avg_quality}</div>
                      <div className="quality-label">Quality Score</div>
                      <div className="quality-indicator">
                        <div 
                          className="quality-fill"
                          style={{ width: `${(year.avg_quality / 10) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'instructor_allotment' && instructorAllotmentData && (
          <div className="analytics-section">
            <div className="section-title-row">
              <h2 className="section-title">Instructor Allotment Analytics</h2>
              <button className="btn btn-primary" onClick={exportInstructorAllotmentCsv}>
                <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.5rem' }} />
                Download CSV
              </button>
            </div>

            <div className="summary-card">
              <div className="summary-content">
                <div className="summary-stat">
                  <div className="stat-value">{instructorAllotmentData.total_instructors || 0}</div>
                  <div className="stat-label">Instructors</div>
                </div>
                <div className="summary-stat">
                  <div className="stat-value">{instructorAllotmentData.total_allotted || 0}</div>
                  <div className="stat-label">Total Allotted Papers</div>
                </div>
                <div className="summary-stat">
                  <div className="stat-value">{instructorAllotmentData.total_reviewed || 0}</div>
                  <div className="stat-label">Total Reviewed Papers</div>
                </div>
              </div>
            </div>

            <div className="table-container">
              <h3 className="table-title">Allotted vs Reviewed Question Papers</h3>
              <div className="data-table">
                <div className="table-header instructor-allotment-header">
                  <div className="table-cell">Instructor</div>
                  <div className="table-cell">Email</div>
                  <div className="table-cell">Allotted</div>
                  <div className="table-cell">Reviewed</div>
                  <div className="table-cell">Pending</div>
                  <div className="table-cell">Completion %</div>
                </div>
                {(instructorAllotmentData.instructor_allotment || []).map((row, index) => (
                  <div key={`${row.instructor_email || row.instructor_name}-${index}`} className="table-row instructor-allotment-row">
                    <div className="table-cell">{row.instructor_name || '-'}</div>
                    <div className="table-cell">{row.instructor_email || '-'}</div>
                    <div className="table-cell">{row.allotted_papers}</div>
                    <div className="table-cell">{row.reviewed_papers}</div>
                    <div className="table-cell">{row.pending_papers}</div>
                    <div className="table-cell">{row.completion_rate}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AnalyticsDashboard
